'use client';

import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { homeFeedHref, pathnameToKind } from '@/constants/home-feed';

import { CondensedHamburguerMenu } from '@repo/ui/components/icons/condensed-hamburguer-menu';
import { Spinner } from '@repo/ui/components/spinner';
import { Button } from '@repo/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu';
import { cn } from '@repo/ui/lib/utils';

import { env } from '@env';
import {
  POST_MESSAGE_DESTINATION_TYPES,
  POST_MESSAGE_ORIGIN_TYPES,
  POST_MESSAGE_TYPES,
} from '@/@types/post-message.types';
import { postMessageBus } from '@/lib/post-message';
import { useAuth } from '@/hooks/use-auth';
import { usePostMessage } from '@/providers/post-message-provider';

type AssistType = 'show' | 'hide';

const INITIAL_ASSIST_TYPE: AssistType = 'show';

const ExtensionDropdown: React.FC = () => {
  const { isAuthenticated, isPending, handleSignIn, handleSignOut } = useAuth();
  const { sendPostMessage } = usePostMessage();

  const [open, setOpen] = useState(false);
  const [highlightType, setHighlightType] = useState<'subtle' | 'strong'>(
    'subtle',
  );
  const [assistType, setAssistType] = useState<AssistType>(INITIAL_ASSIST_TYPE);

  const assistTypeRef = useRef<AssistType>(INITIAL_ASSIST_TYPE);
  const highlightTypeRef = useRef<'subtle' | 'strong'>('subtle');

  const onAssistTypeChange = (type: AssistType) => {
    assistTypeRef.current = type;
    setAssistType(type);
    sendPostMessage({
      origin: POST_MESSAGE_ORIGIN_TYPES.FROM_WEBAPP,
      to: POST_MESSAGE_DESTINATION_TYPES.TO_PUBLISHER,
      type: POST_MESSAGE_TYPES.ARTICLE_FILTER_CONTROLS_VISIBILITY,
      message: type,
    });
  };

  const onHighlightTypeChange = (type: 'subtle' | 'strong') => {
    if (assistType === 'hide') return;
    highlightTypeRef.current = type;
    setHighlightType(type);
    sendPostMessage({
      origin: POST_MESSAGE_ORIGIN_TYPES.FROM_WEBAPP,
      to: POST_MESSAGE_DESTINATION_TYPES.TO_PUBLISHER,
      type: POST_MESSAGE_TYPES.HIGHLIGHT_FONT_WEIGHT,
      message: type,
    });
  };

  useEffect(() => {
    highlightTypeRef.current = highlightType;
  }, [highlightType]);

  useEffect(() => {
    const unsubAssist = postMessageBus.subscribe(
      POST_MESSAGE_TYPES.ARTICLE_FILTER_CONTROLS_VISIBILITY,
      ({ message }) => {
        if (
          (message === 'hide' || message === 'show') &&
          assistTypeRef.current !== message
        ) {
          assistTypeRef.current = message;
          setAssistType(message);
        }
      },
    );

    const unsubHighlight = postMessageBus.subscribe(
      POST_MESSAGE_TYPES.HIGHLIGHT_FONT_WEIGHT,
      ({ message }) => {
        if (
          (message === 'subtle' || message === 'strong') &&
          highlightTypeRef.current !== message
        ) {
          highlightTypeRef.current = message;
          setHighlightType(message);
        }
      },
    );

    return () => {
      unsubAssist();
      unsubHighlight();
    };
  }, []);

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-auto w-[22px] text-white overflow-hidden p-0 hover:bg-transparent [&_svg]:size-[21px] cred-md:[&_svg]:size-[22px] cred-lg:[&_svg]:size-[26px] cred-xl:[&_svg]:size-[33px] hover:text-white"
        >
          <span className="sr-only">Toggle menu</span>
          <CondensedHamburguerMenu />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent
          align="end"
          className="dark min-w-[244px] rounded-md border-none bg-[#2D3748] p-0 text-[#F0F0F0] cred-md:min-w-[290px] cred-lg:min-w-[300px] cred-xl:min-w-cred-md"
        >
          <DropdownMenuItem
            onClick={(e) => e.preventDefault()}
            className="flex-col items-start px-1.5 pt-1.5 focus:bg-transparent focus:text-foreground"
          >
            <div className="w-full space-y-1.5 rounded-[5.33px] bg-[#1E2A3B] p-[5px] cred-md:space-y-2 cred-md:p-[7px]">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium italic leading-[14px] cred-md:text-sm cred-md:leading-[19px]">
                  on-page assist
                </span>
                <div className="flex w-[129px] gap-1 rounded-[5px] bg-black p-0.5 cred-md:w-[150px] cred-md:gap-1.5 cred-md:p-1">
                  <Button
                    variant="ghost"
                    onClick={() => onAssistTypeChange('show')}
                    className={cn(
                      'h-auto w-full rounded px-2.5 py-[5px] text-xs text-[#C0C0C0] hover:bg-[#485D77] active:bg-[#1A3456] cred-md:px-[10px] cred-md:py-[4px] cred-md:text-sm cred-md:leading-4',
                      assistType === 'show' &&
                        'bg-cta-dark text-[#F0F0F0] hover:bg-cta-dark',
                    )}
                  >
                    Show
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => onAssistTypeChange('hide')}
                    className={cn(
                      'h-auto w-full rounded px-2.5 py-[5px] text-xs text-[#C0C0C0] hover:bg-[#485D77] active:bg-[#1A3456] cred-md:px-[10px] cred-md:py-[4px] cred-md:text-sm cred-md:leading-4',
                      assistType === 'hide' &&
                        'bg-cta-dark text-[#F0F0F0] hover:bg-cta-dark',
                    )}
                  >
                    Hide
                  </Button>
                </div>
              </div>
              <div
                className={cn(
                  'flex items-center justify-between transition-colors',
                  assistType === 'hide' && 'cursor-not-allowed',
                )}
              >
                <span
                  className={cn(
                    'text-[10px] leading-3 transition-colors cred-md:text-xs cred-md:leading-[14px]',
                    assistType === 'hide' && 'text-[#A0A0A0]',
                  )}
                >
                  In-Page Highlights
                </span>
                <div
                  className={cn(
                    'flex w-[129px] gap-1 rounded-[5px] p-0.5 transition-colors cred-md:w-[150px] cred-md:gap-1.5 cred-md:p-1',
                    assistType === 'hide' ? 'bg-[#292929]' : 'bg-black',
                  )}
                >
                  <Button
                    disabled={assistType === 'hide'}
                    variant="ghost"
                    onClick={() => {
                      if (assistType === 'hide') return;
                      onHighlightTypeChange('subtle');
                    }}
                    className={cn(
                      'h-auto w-full rounded px-2.5 py-[5px] text-xs text-[#C0C0C0] hover:bg-[#485D77] active:bg-[#1A3456] disabled:opacity-100 cred-md:px-[10px] cred-md:py-[4px] cred-md:text-sm cred-md:leading-4',
                      highlightType === 'subtle' &&
                        'bg-cta-dark text-[#F0F0F0] hover:bg-cta-dark disabled:bg-[#383838]',
                    )}
                  >
                    Subtle
                  </Button>
                  <Button
                    disabled={assistType === 'hide'}
                    variant="ghost"
                    onClick={() => {
                      if (assistType === 'hide') return;
                      onHighlightTypeChange('strong');
                    }}
                    className={cn(
                      'h-auto w-full rounded px-2.5 py-[5px] text-xs text-[#C0C0C0] hover:bg-[#485D77] active:bg-[#1A3456] disabled:opacity-100 cred-md:px-[10px] cred-md:py-[4px] cred-md:text-sm cred-md:leading-4',
                      highlightType === 'strong' &&
                        'bg-cta-dark text-[#F0F0F0] hover:bg-cta-dark disabled:bg-[#383838]',
                    )}
                  >
                    Strong
                  </Button>
                </div>
              </div>
            </div>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="mx-1.5 my-0 bg-[#C0C0C0]" />
          {isAuthenticated && (
            <DropdownMenuItem
              disabled={isPending}
              onClick={(e) => {
                e.preventDefault();
                setOpen(false);
                window.open(
                  `${env.NEXT_PUBLIC_WEBSITE_APP_URL}/delete-account`,
                  '_blank',
                  'noopener,noreferrer',
                );
              }}
              className="cursor-pointer rounded-b-md rounded-t-none border-none px-[calc(6px+5.33px)] pb-[calc(6px+3.33px)] pt-[calc(4px+3.33px)] focus:bg-[#1D2735] cred-md:px-[14px] cred-md:text-sm"
            >
              Manage Account
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={isPending}
            onClick={async (e) => {
              e.preventDefault();
              if (isAuthenticated) {
                await handleSignOut();
              } else {
                handleSignIn(
                  e as unknown as React.MouseEvent<
                    HTMLButtonElement,
                    MouseEvent
                  >,
                );
              }
              setOpen(false);
            }}
            className="cursor-pointer rounded-b-md rounded-t-none border-none px-[calc(6px+5.33px)] pb-[calc(6px+3.33px)] pt-[calc(4px+3.33px)] focus:bg-[#1D2735] cred-md:px-[14px] cred-md:text-sm"
          >
            {isAuthenticated ? 'Logout' : 'Sign in'}
            {isPending && <Spinner size={5} className="ml-auto" />}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
};

const InnerAppHeader: React.FC = () => {
  const pathname = usePathname();

  // Feed kind is the route now, so the logo returns to the videos feed only
  // from a video surface.
  const isYoutubePage = useMemo(
    () =>
      pathnameToKind(pathname) === 'videos' || pathname.startsWith('/videos'),
    [pathname],
  );

  return (
    <header className="sticky top-0 z-20 flex h-[39px] w-full items-center border-b border-border/50 bg-[#0A2342] py-[5.5px] pl-3 pr-2 text-white cred-md:h-[46px] cred-lg:h-[49px] cred-xl:h-16">
      <div className="relative flex w-full items-center justify-between gap-3 cred-md:gap-3.5 cred-lg:gap-[15px] cred-xl:gap-4">
        <Button
          asChild
          variant="ghost"
          className="w-auto p-0 hover:bg-transparent h-auto"
        >
          <Link href={isYoutubePage ? homeFeedHref('videos') : homeFeedHref()}>
            <Image
              src="/images/home-white.png"
              alt="Skim Logo"
              width={1600}
              height={1600}
              className="aspect-square shrink-0 object-contain size-7 cred-md:size-[30px] cred-lg:size-8 cred-xl:size-[34px]"
            />
          </Link>
        </Button>
        <Link
          href={isYoutubePage ? homeFeedHref('videos') : homeFeedHref()}
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
        >
          <Image
            src="/images/skim-mobile-header-logo.png"
            alt="Skim Logo"
            priority
            width={2112} // 704 x 3 = 2112
            height={420} // 140 x 3 = 420
            className="aspect-704/140 shrink-0 overflow-hidden w-auto h-7 cred-md:h-9 cred-lg:h-[38px] cred-xl:h-12 object-contain"
          />
        </Link>
        <div className="w-full flex items-center justify-end">
          {/* Website Sidebar or Extension Dropdown */}
          <ExtensionDropdown />
        </div>
      </div>
    </header>
  );
};

export const AppHeader: React.FC = () => {
  return (
    <Suspense fallback={<></>}>
      <InnerAppHeader />
    </Suspense>
  );
};
